// Venue-request support tool. An owner at their plan's venue limit asks the
// founder for another location; the request lands here so it can be worked
// (open the org, email an invoice or payment link, raise the org's venue
// limit, then mark the request fulfilled — or declined if it won't go
// ahead). Mirrors the API's venue-requests admin controller — server is
// authoritative for shapes; kept in sync by hand.

import { apiGet, apiPatch } from './client';

export type VenueRequestStatus = 'open' | 'fulfilled' | 'declined';

/** One request as the admin console sees it — includes who's asking, which
 * the owner-facing `GET /organizations/:orgId/venue-requests` list omits
 * because the owner already knows. */
export interface AdminVenueRequestRow {
  id: string;
  organizationId: string;
  organizationName: string;
  ownerEmail: string | null;
  locationName: string;
  city: string | null;
  region: string | null;
  note: string | null;
  status: VenueRequestStatus;
  createdAt: string;
}

export interface ListVenueRequestsResult {
  requests: AdminVenueRequestRow[];
}

/** GET /admin/venue-requests — omit `status` to see every request. */
export function listVenueRequests(
  status?: VenueRequestStatus,
): Promise<ListVenueRequestsResult> {
  return apiGet<ListVenueRequestsResult>(
    '/admin/venue-requests',
    status ? { status } : {},
  );
}

/**
 * PATCH /admin/venue-requests/:id — resolve a request once the founder has
 * acted on it (or hasn't). No response body is depended on here; a non-error
 * response is the signal, and the caller re-fetches the list.
 */
export function setVenueRequestStatus(
  id: string,
  status: Extract<VenueRequestStatus, 'fulfilled' | 'declined'>,
): Promise<void> {
  return apiPatch<void>(`/admin/venue-requests/${encodeURIComponent(id)}`, {
    status,
  });
}

/**
 * PATCH /admin/organizations/:orgId/venue-limit — set an override on
 * Organization.maxVenues, or clear it (null) back to the plan default.
 */
export function setOrganizationVenueLimit(
  orgId: string,
  maxVenues: number | null,
): Promise<void> {
  return apiPatch<void>(
    `/admin/organizations/${encodeURIComponent(orgId)}/venue-limit`,
    { maxVenues },
  );
}
